import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Edit3, Loader2, FileText, Upload, PenTool,
  BookOpen, MessageSquare, Sparkles, FileUp, Languages
} from "lucide-react";
import ToneSelector from "@/components/ai-editor/ToneSelector";
import ComparisonView from "@/components/ai-editor/ComparisonView";
import ReadabilityGauge from "@/components/ai-editor/ReadabilityGauge";
import StyleGuideManager from "@/components/ai-editor/StyleGuideManager";
import EditorChat from "@/components/ai-editor/EditorChat";
import LanguageSelect from "@/components/LanguageSelect";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AIEditorHub() {
  const [activeTab, setActiveTab] = useState("editor");

  // Editor state
  const [text, setText] = useState("");
  const [tone, setTone] = useState("professional");
  const [editorLanguage, setEditorLanguage] = useState("en");
  const [editing, setEditing] = useState(false);
  const [result, setResult] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  // Readability state
  const [readability, setReadability] = useState(null);
  const [readabilityLoading, setReadabilityLoading] = useState(false);

  // File upload for editor
  const fileInputRef = useRef(null);

  const handleEdit = async () => {
    if (!text.trim()) {
      toast.error("Please enter or paste some text");
      return;
    }
    setEditing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/api/ai-editor/edit`, {
        text: text,
        tone: tone,
        language: editorLanguage,
      }, {
        headers: getHeaders(), withCredentials: true
      });
      setResult(res.data);
      setShowComparison(false);
      toast.success("Edit complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Edit failed");
    }
    setEditing(false);
  };

  const handleCorrect = async () => {
    if (!text.trim()) return;
    setEditing(true);
    try {
      const res = await axios.post(`${API}/api/ai-editor/correct`, {
        text: text,
        tone: tone,
      }, {
        headers: getHeaders(), withCredentials: true
      });
      setResult({
        original: text,
        corrected: res.data.corrected,
        changes: res.data.changes,
        tone_adjusted: res.data.corrected,
        tone_used: "correction",
      });
      setShowComparison(false);
      toast.success(`${res.data.changes?.length || 0} correction(s) applied`);
    } catch (err) {
      toast.error("Correction failed");
    }
    setEditing(false);
  };

  const handleReadability = async () => {
    if (!text.trim()) return;
    setReadabilityLoading(true);
    try {
      const res = await axios.post(`${API}/api/ai-editor/readability`, {
        text: text,
      }, {
        headers: getHeaders(), withCredentials: true
      });
      setReadability(res.data);
    } catch {}
    setReadabilityLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/api/ai-editor/extract-text`, formData, {
        headers: getHeaders(), withCredentials: true
      });
      setText(res.data.text || "");
      toast.success("File text extracted");
    } catch (err) {
      // Fallback: try reading as text
      const reader = new FileReader();
      reader.onload = (ev) => {
        setText(ev.target?.result || "");
        toast.success("File loaded");
      };
      reader.readAsText(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleInsertFromChat = (content) => {
    setText(content);
    setActiveTab("editor");
    toast.success("AI response inserted into editor");
  };

  const acceptResult = () => {
    if (result?.tone_adjusted) {
      setText(result.tone_adjusted);
      setResult(null);
      toast.success("Changes applied to editor");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
            <PenTool className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
              AI EDITOR
            </h1>
            <p className="text-slate-500">Spelling, grammar, tone & readability — powered by DeepSeek</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex border-b border-slate-200">
          {[
            { id: "editor", label: "Editor", icon: Edit3 },
            { id: "style-guide", label: "Style Guide", icon: BookOpen },
            { id: "chat", label: "AI Chat", icon: MessageSquare },
          ].map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-violet-500 text-violet-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === "editor" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Input + Controls */}
          <div className="lg:col-span-2 space-y-4">
            {/* Text Input */}
            <div className="bg-white border border-slate-200 rounded">
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-app">
                <span className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Input Text
                  {text && <span className="text-slate-400">({text.split(/\s+/).filter(Boolean).length} words)</span>}
                </span>
                <div className="flex gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.docx,.pdf,.md,.html"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs text-slate-500 hover:text-violet-600 flex items-center gap-1"
                  >
                    <FileUp className="w-3 h-3" />
                    Open File
                  </button>
                </div>
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste or type your text here to edit..."
                rows={12}
                className="w-full p-4 text-sm text-slate-700 resize-y focus:outline-none min-h-[200px]"
              />
            </div>

            {/* Controls */}
            <div className="bg-white border border-slate-200 rounded p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ToneSelector value={tone} onChange={setTone} />
          </div>

          <div className="flex items-center gap-2 mt-2">
            <Languages className="w-4 h-4 text-violet-500" />
            <span className="text-xs font-medium text-slate-600">Language:</span>
            <LanguageSelect value={editorLanguage} onValueChange={setEditorLanguage}
              includeAuto={false} />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Actions</label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handleEdit}
                      disabled={editing || !text.trim()}
                      className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 rounded-none text-sm"
                    >
                      {editing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      Edit with AI
                    </Button>
                    <Button
                      onClick={handleCorrect}
                      disabled={editing || !text.trim()}
                      variant="outline"
                      className="rounded-none text-sm border-slate-300"
                    >
                      <PenTool className="w-4 h-4 mr-1" />
                      Correct Only
                    </Button>
                    <Button
                      onClick={() => { handleReadability(); }}
                      disabled={readabilityLoading || !text.trim()}
                      variant="outline"
                      className="rounded-none text-sm border-slate-300"
                    >
                      {readabilityLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                      Readability
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Result */}
            {result && (
              <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-violet-50 to-purple-50 border-b border-slate-200">
                  <span className="text-sm font-medium text-violet-700 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4" />
                    {result.tone_used === "correction" ? "Corrected" : `Edited — ${result.tone_used} tone`}
                    {result.changes?.length > 0 && (
                      <span className="text-xs text-slate-500 font-normal">({result.changes.length} change{result.changes.length !== 1 ? "s" : ""})</span>
                    )}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowComparison(!showComparison)}
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs h-7 border-slate-300"
                    >
                      {showComparison ? "Show Result" : "Compare"}
                    </Button>
                    <Button
                      onClick={acceptResult}
                      size="sm"
                      className="bg-violet-600 hover:bg-violet-500 rounded-none text-xs h-7"
                    >
                      Accept
                    </Button>
                  </div>
                </div>
                <div className="p-4">
                  {showComparison ? (
                    <ComparisonView
                      original={result.original || text}
                      edited={result.tone_adjusted}
                      changes={result.changes}
                    />
                  ) : (
                    <div>
                      <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">
                        {result.tone_adjusted}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right: Readability + Info */}
          <div className="space-y-4">
            <ReadabilityGauge score={readability} />

            {/* Quick stats */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Quick Tips</h4>
              <ul className="space-y-2 text-xs text-slate-600">
                <li className="flex items-start gap-2">
                  <Sparkles className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />
                  <span><strong>Edit with AI</strong> — Full pipeline: correct then apply tone</span>
                </li>
                <li className="flex items-start gap-2">
                  <PenTool className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />
                  <span><strong>Correct Only</strong> — Just fix spelling &amp; grammar</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />
                  <span><strong>Readability</strong> — See Flesch-Kincaid score &amp; reading level</span>
                </li>
                <li className="flex items-start gap-2">
                  <BookOpen className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />
                  <span><strong>Style Guide</strong> — Train AI on your organisation's preferred terms</span>
                </li>
                <li className="flex items-start gap-2">
                  <Upload className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />
                  <span><strong>Open File</strong> — Load .txt, .docx, .pdf, .md files</span>
                </li>
              </ul>
            </div>

            {/* Detected tone */}
            {text.trim().length > 50 && (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Current Text</h4>
                <p className="text-xs text-slate-500 line-clamp-3">{text.slice(0, 200)}...</p>
                <p className="text-xs text-slate-400 mt-1">{text.split(/\s+/).filter(Boolean).length} words · {text.length} characters</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "style-guide" && (
        <div className="max-w-3xl">
          <div className="bg-white border border-slate-200 rounded p-6">
            <h2 className="font-semibold text-lg text-slate-800 mb-1">Organisational Style Guide</h2>
            <p className="text-sm text-slate-500 mb-6">
              Manage preferred terms, banned words, and replacements. These rules automatically
              override AI suggestions during editing.
            </p>
            <StyleGuideManager />
          </div>
        </div>
      )}

      {activeTab === "chat" && (
        <div className="max-w-3xl">
          <div className="bg-white border border-slate-200 rounded p-6">
            <h2 className="font-semibold text-lg text-slate-800 mb-1">Editor AI Assistant</h2>
            <p className="text-sm text-slate-500 mb-4">
              Ask the AI to rewrite, summarise, or analyse your text. Responses can be inserted
              directly into the editor.
            </p>
            <EditorChat onInsertText={handleInsertFromChat} />
          </div>
        </div>
      )}
    </div>
  );
}
